import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import AmplifyToggle from '@amplify/amplify-toggle';
import isEmpty from 'lodash.isempty';
import config from '../../../configs/reading';
import { selectors } from '../../../root/reading/root';
import { switchPeriod } from '../../../api/periods/shared/periodsActions';
import {
    getGridProps,
    getRowsToRender,
} from '../../../components/resultsTable/shared/resultsHelper';
import {
    compareFunction,
    mapSkillNamesToRowObject,
} from '../../../components/resultsTable/reading/resultsHelper';
import CommonCellBuilder from '../../../components/cell/reading/CommonCellBuilder';
import LinkWrapper from '../../../features/navigation/LinkWrapper';
import Benchmark from '../../../features/benchmark/Benchmark';
import switchGrade from '../../../api/featureReport/featureReportActions';
import { setPercentileToggleState } from '../../../api/percentiles/percentilesActions';
import {
    getBasicComprehensionIconsGroup,
    buildBenchmarkLegendSections,
    isBasicComprehensionSectionVisible,
} from '../../../features/legend/shared/legendUtils';
import buildFAQlist from '../../../features/legend/shared/FAQSectionUtils';
import getAvailibleQuestions from '../../../utils/reading/FAQSectionUtils';
import { toyTabs } from '../../../utils/shared/navigationUtils';
import { getUrlParams } from '../../../utils/shared/urlUtils';
import { LICENSE, PERIOD, TITLE } from '../../../constants/shared/CommonConstants';
import {
    DROPDOWN_ITEMS_LENGTH_EQUAL_ONE,
    GRADE,
    basicComprehensionGradesMap,
} from '../../../constants/shared/GradesConstants';
import { D8_MEASURES, TRC_OL_MEASURES } from '../../../constants/shared/MeasuresConstants';
import { BASE_URLS, SS_URL } from '../../../constants/reading/ApiConstants';
import openProbeDetails from '../../../api/probeDetails/probeDetailsActions';
import ProbeDetailsPageContainer from '../../probeDetails/reading/ProbeDetailsPageContainer';
import { openBasicComprehensionWebEntry } from '../../../api/basicComprehensionWebEntry/shared/basicComprehensionWebEntryActions';
import MazeWebEntryContainer from '../../probeDetails/reading/mazeWebEntry/MazeWebEntryContainer';
import { setLexileToggleState } from '../../../api/lexile/lexileActions';
import { isAvailableLexileToggle } from '../../../constants/reading/LexileGradeMapper';
import { lexileLookerEvent } from '../../../api/lrs/reading/lrsActions';
import { percentileLookerEvent } from '../../../api/lrs/shared/lrsActions';
import { findSecure } from '../../../utils/shared/filterUtils';
import { showSecondAttemptBulkInvalidationModal } from '../../../api/modals/reading/modalsActions';
import SecondAttemptBulkInvalidationModalContainer from '../../modals/reading/secondAttemptBulkInvalidationModal/SecondAttemptBulkInvalidationModalContainer';

const mapStateToProps = state => {
    const location = selectors.getLocation(state);
    const selectedPeriod = selectors.getSelectedPeriod(state);
    const licensingInfo = selectors.getLicensingInfo(state);
    const isFeatureReportEnabled = config.views.benchmark.featureReportEnabled();
    const gradesInCurrentClass = selectors.getGradesInCurrentClass(state);
    const isD8License = licensingInfo.includes(LICENSE.D8);
    const gradeToMeasureMapper = selectors.getGradeToMeasureMapper(state);
    const { GRADES_WITHOUT_BC, GRADES_WITH_BC } = basicComprehensionGradesMap(gradeToMeasureMapper);
    const isBasicComprehensionIconsVisible =
        isBasicComprehensionSectionVisible(
            selectedPeriod,
            gradesInCurrentClass,
            gradeToMeasureMapper,
            GRADES_WITHOUT_BC,
        ) && isD8License;
    const basicComprehensionIconsGroup =
        isBasicComprehensionIconsVisible && getBasicComprehensionIconsGroup('Observational Maze');
    const hasRiskIndicator = true;

    const isResultById =
        !!getUrlParams(location)?.resultId &&
        !isEmpty(selectors.getResultById(state, getUrlParams(location)?.resultId));
    const benchmarkData = selectors.getBenchmarkData(state);
    const isSecureFound = findSecure(benchmarkData?.rows);

    return {
        location,
        licensingInfo,
        selectedPeriod,
        grades: selectors.getGradesInCurrentClass(state),
        benchmarkData,
        areTogglesEnabled: selectors.areTogglesEnabled(state),
        isPercentileShown: selectors.isPercentileShown(state),
        areResultsUpdating: selectors.areResultsUpdating(state),
        isDemoModeOn: selectors.getIsDemoModeOn(state),
        demoClass: selectors.getDemoClass(state),
        tableSortBy: {
            type: 'customSort',
            compareFunction,
        },
        gridProps: getGridProps(
            selectedPeriod,
            selectors.getToyPeriods(state),
            selectors.getResultsTableSummaryMeasures(state),
        ),
        selectedGrade: selectors.getSelectedGrade(state),
        currentPeriod: selectors.getCurrentPeriod(state),
        tabs: toyTabs(
            location,
            BASE_URLS.BENCHMARK_URL,
            {
                burstGroupUid: getUrlParams(location).burstGroupUid,
            },
            selectors.getToyPeriods(state).includes(PERIOD.SUMMER),
            selectors.getSummerPeriodLabel(state),
        ),
        legendSections: buildBenchmarkLegendSections(
            selectors.getLicensingInfo(state),
            selectors.hasTKInCurrentClass(state),
            buildFAQlist(
                location,
                selectors.getLicensingInfo(state),
                getAvailibleQuestions,
                selectors.getSelectedPeriod(state),
                isSecureFound,
            ),
            LICENSE.TRC,
            basicComprehensionIconsGroup,
            hasRiskIndicator,
            isSecureFound,
        ),
        classNameNavWrapper: 'd-flex justify-content-between',
        LinkWrapper,
        isLexileShown: selectors.isLexileShown(state),
        featureReportConfig: {
            isFeatureReportEnabled,
            featureReport:
                isFeatureReportEnabled && config.views.benchmark.getFeatureReport(licensingInfo),
        },
        GRADES_WITH_BC,
        GRADES_WITHOUT_BC,
        isResultById,
        summerPeriodLabel: selectors.getSummerPeriodLabel(state),
        isResetButtonAvailableInClass: selectors.isResetButtonAvailableInClass(state),
        isSecondAttemptBulkInvalidationModalShown:
            selectors.isSecondAttemptBulkInvalidationModalShown(state),
    };
};
const mapDispatchToProps = dispatch => ({
    switchGrade: grade => {
        dispatch(switchGrade(grade));
    },
    switchPeriod: period => {
        dispatch(switchPeriod(period));
    },
    switchPercentileToggle: ({ currentTarget }) => {
        dispatch(setPercentileToggleState(currentTarget.checked));
        dispatch(percentileLookerEvent(currentTarget.checked));
    },
    switchLexileToggle: ({ currentTarget }) => {
        dispatch(setLexileToggleState(currentTarget.checked));
        dispatch(lexileLookerEvent(currentTarget.checked));
    },
    openProbeDetails: resultId => dispatch(openProbeDetails(resultId)),
    openAdditionalPanel: () => dispatch(openBasicComprehensionWebEntry(true)),
    showSecondAttemptBulkInvalidationModal: studentInfo =>
        dispatch(showSecondAttemptBulkInvalidationModal(studentInfo)),
});
const mergeProps = (stateProps, dispatchProps, ownProps) => {
    const dropdownGrades = stateProps.grades.concat(
        stateProps.grades.length > DROPDOWN_ITEMS_LENGTH_EQUAL_ONE ? GRADE.ALL_GRADES : [],
    );
    const { resultId, webEntryOpened } = getUrlParams(stateProps.location);
    const {
        isDemoModeOn,
        isResultById,
        GRADES_WITH_BC,
        GRADES_WITHOUT_BC,
        summerPeriodLabel,
        isResetButtonAvailableInClass,
        isSecondAttemptBulkInvalidationModalShown,
    } = stateProps;

    return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        dropdownGrades,
        tableRows: [
            ...getRowsToRender({
                selectedPeriod: stateProps.selectedPeriod,
                location: stateProps.location,
                benchmarkData: stateProps.benchmarkData,
                columnsGroup: [D8_MEASURES, TRC_OL_MEASURES],
                LinkElement: Link,
                baseUrl: SS_URL.BENCHMARK_HISTORY,
                isDemoModeOn,
                CommonCellBuilder,
                additionalParams: {
                    burstGroupUid: getUrlParams(stateProps.location).burstGroupUid,
                },
                isPercentileShown: stateProps.isPercentileShown,
                openProbeDetails: dispatchProps.openProbeDetails,
                openAdditionalPanel: dispatchProps.openAdditionalPanel,
                tabelHeadersCells: mapSkillNamesToRowObject(
                    stateProps.benchmarkData.tableMeasures,
                    [D8_MEASURES, TRC_OL_MEASURES],
                    stateProps.isLexileShown,
                    isResetButtonAvailableInClass,
                ),
                isPercentileToggleOn:
                    stateProps.areTogglesEnabled.percentile && stateProps.isPercentileShown,
                summerPeriodLabel,
                isResetButtonAvailableInClass,
                showSecondAttemptBulkInvalidationModal:
                    dispatchProps.showSecondAttemptBulkInvalidationModal,
            }),
        ],
        widgets: [
            isAvailableLexileToggle(stateProps.grades, stateProps.licensingInfo) && (
                <AmplifyToggle
                    key="lexile"
                    id="Lexile"
                    labelText="Lexile"
                    enabledText="On"
                    disabledText="Off"
                    onChange={dispatchProps.switchLexileToggle}
                    value={stateProps.isLexileShown}
                    isAvailable={stateProps.areTogglesEnabled.lexile}
                />
            ),
            stateProps.licensingInfo.includes(LICENSE.D8) && (
                <AmplifyToggle
                    key="percentile"
                    id="Percentile"
                    labelText="Percentile"
                    enabledText="On"
                    disabledText="Off"
                    onChange={dispatchProps.switchPercentileToggle}
                    value={stateProps.isPercentileShown}
                    isAvailable={stateProps.areTogglesEnabled.percentile}
                />
            ),
        ],
        wrappers: (
            <>
                {/* Show ProbeDetailsPageContainer only in case there's data by the given id */}
                {isResultById && <ProbeDetailsPageContainer resultId={resultId} />}
                {webEntryOpened && (
                    <MazeWebEntryContainer
                        panelType={TITLE.BENCHMARK_TITLE}
                        gradesWithoutBC={GRADES_WITHOUT_BC}
                        gradesWithBC={GRADES_WITH_BC}
                    />
                )}
                {isSecondAttemptBulkInvalidationModalShown && (
                    <SecondAttemptBulkInvalidationModalContainer />
                )}
            </>
        ),
    };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(Benchmark);
